(() => {
  'use strict';
  const drawer = document.getElementById('fractal-cart');
  if (!drawer || window.FractalCart) return;
  const root = window.Shopify?.routes?.root || '/';
  const $ = (selector) => drawer.querySelector(selector);
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
  const safeURL = (value) => {
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) ? escape(url.href) : ''; } catch (_) { return ''; }
  };
  const productURL = (handle) => `${root}products/${encodeURIComponent(handle)}`;
  let cart = null;
  let busy = false;
  let refreshPending = false;
  let revision = 0;
  let returnFocus = null;
  const products = new Map();
  const readSetting = (name) => { try { return JSON.parse(drawer.dataset[name] || 'null'); } catch (_) { return null; } };
  const configPromise = fetch(drawer.dataset.configUrl, { signal:AbortSignal.timeout(8000) }).then((r) => {
    if (!r.ok) throw new Error('Configuration unavailable');
    return r.json();
  }).catch(() => null);
  let bundleMinimum = 3;
  const bundleRulesPromise = fetch(root + 'apps/subscriptions/bundle-settings', { signal:AbortSignal.timeout(8000) })
    .then((r) => r.ok ? r.json() : null).then((rules) => {
      if (Number.isInteger(rules?.minimumItems) && rules.minimumItems > 0) bundleMinimum = rules.minimumItems;
    }).catch(() => {});
  let config = null;
  configPromise.then((value) => {
    config = value;
    if (!config) return;
    config.design = { ...(config.design || {}), ...(readSetting('cartDesign') || {}) };
    const savedRewards = readSetting('cartRewards');
    if (savedRewards && Array.isArray(savedRewards.rewards)) { config.rewards = savedRewards.rewards; config.currency = savedRewards.currency; }
    applyDesign();
  });
  const hex = (value) => /^#[a-f0-9]{6}$/i.test(value || '');
  const imageURL = (value) => /^data:image\/(svg\+xml|png);base64,[a-z0-9+/]+=*$/i.test(value || '') ? value : '';
  const FONTS = { serif:"'Stempel Garamond LT Pro',Georgia,serif", system:'system-ui,-apple-system,sans-serif', mono:"'Courier Prime','Courier New',monospace" };
  const ICONS = {
    truck:'M3 7h11v9H3zM14 10h4l3 3v3h-7zM6 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM18 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
    tag:'M6 7h.01M4 4h7l9 9-7 7-9-9V4z',
    gift:'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7a3 3 0 1 0-3-3c0 2 3 3 3 3zM12 7a3 3 0 1 1 3-3c0 2-3 3-3 3z',
    bag:'M6 7h12l-1 14H7zM9 7V5a3 3 0 0 1 6 0v2',
    star:'M12 2l3 7 7 .6-5.3 4.6L18.4 22 12 18.3 5.6 22l1.7-7.8L2 9.6 9 9z',
    heart:'M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 1 0-7.1 7.1L12 21.5l8.8-8.8a5 5 0 0 0 0-7.1z',
    leaf:'M11 20A7 7 0 0 1 4 13c0-7 7-9 16-9-1 9-3 16-9 16zM4 20c3-3 6-5 10-7',
    percent:'M19 5L5 19M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM16.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  };
  const icon = (name) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${ICONS[name] || ICONS.bag}"/></svg>`;
  // Merchant templates are trusted admin input, but still never get to run script on the storefront.
  function sanitize(html) {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    doc.body.querySelectorAll('script,iframe,object,embed,link,meta,base,form').forEach((el) => el.remove());
    doc.body.querySelectorAll('*').forEach((el) => [...el.attributes].forEach((a) => {
      if (/^on/i.test(a.name) || /^\s*(javascript|vbscript|data):/i.test(a.value) && /^(href|src|xlink:href|action|formaction)$/i.test(a.name)) el.removeAttribute(a.name);
    }));
    return doc.body.innerHTML;
  }
  const fill = (template, tokens) => sanitize(String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => name in tokens ? `%%fc-token-${name}%%` : match))
    .replace(/%%fc-token-(\w+)%%/g, (_, name) => tokens[name]);
  function logoMarkup(d) {
    const logo = d.logoMode === 'image' && imageURL(d.logoImage);
    return logo ? `<img class="fc__logo" src="${logo}" alt="${escape(d.brand || 'Store logo')}" style="height:${Math.max(16, Math.min(48, Number(d.logoHeight) || 28))}px">` : `<span class="fc__eyebrow">${escape(d.brand || 'FRACTAL FOREST')}</span>`;
  }
  function applyDesign() {
    const d = config?.design || {};
    const vars = { background:'--fc-bg', text:'--fc-ink', accent:'--fc-green', buttonText:'--fc-button-ink', line:'--fc-line', progressFill:'--fc-progress', progressTrack:'--fc-track', ringColor:'--fc-ring', addInk:'--fc-add-ink' };
    for (const [key, property] of Object.entries(vars)) if (hex(d[key])) drawer.style.setProperty(property, d[key]);
    const solid = d.fill === 'solid';
    if (hex(d.checkoutEnd)) drawer.style.setProperty('--fc-checkout-bg', solid || !hex(d.checkoutStart) ? d.checkoutEnd : `linear-gradient(90deg,${d.checkoutStart},${d.checkoutEnd})`);
    if (hex(d.addEnd)) drawer.style.setProperty('--fc-add-bg', solid || !hex(d.addStart) ? d.addEnd : `linear-gradient(90deg,${d.addStart},${d.addEnd})`);
    if (FONTS[d.font]) drawer.style.setProperty('--fc-font', FONTS[d.font]);
    if (Number.isFinite(d.width)) drawer.style.setProperty('--fc-width', `${Math.max(360, Math.min(600, d.width))}px`);
    if (Number.isFinite(d.radius)) drawer.style.setProperty('--fc-radius', `${Math.max(0, Math.min(999, d.radius))}px`);
    const brand = $('.fc__header>div');
    if (d.headerTemplateEnabled && d.headerTemplate) {
      brand.innerHTML = fill(d.headerTemplate, { logo:d.hideTitle ? '' : logoMarkup(d), title:escape(d.title || 'Your cart'), count:'<span data-fc-count></span>' });
    } else {
      brand.innerHTML = d.hideTitle ? '' : logoMarkup(d);
    }
    // The accessible cart title always exists, even when hidden or templated.
    const level = ['h1', 'h2', 'h3', 'p'].includes(d.headingLevel) ? d.headingLevel : 'h2';
    const heading = document.createElement(level);
    heading.id = 'fc-title'; heading.className = 'fc__sr';
    heading.innerHTML = `${escape(d.title || 'Your cart')} <span data-fc-count></span>`;
    brand.append(heading);
    $('.fc__header').classList.toggle('fc__header--center', d.alignment === 'center');
    drawer.classList.toggle('fc--qty-price', d.qtyPosition === 'price');
    drawer.classList.toggle('fc--no-variants', d.showVariants === false);
    if (cart) drawer.querySelectorAll('[data-fc-count]').forEach((el) => { el.textContent = `(${cart.item_count})`; });
    const announcement = $('[data-fc-announcement]');
    announcement.textContent = d.announcement || ''; announcement.hidden = !d.announcement;
    if (d.checkoutLabel) { $('[data-fc-checkout]').textContent = d.checkoutLabel; $('[data-fc-checkout]').value = d.checkoutLabel; }
    const express = $('.fc__express'); if (express) express.hidden = d.showExpress === false;
  }
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:cart?.currency || 'USD' }).format((Number(cents) || 0) / 100);
  const isBundle = (item) => Boolean(item.properties?._fractal_bundle || item.properties?._Bundle || /^Bundle delivery /i.test(item.selling_plan_allocation?.selling_plan?.name || ''));
  function bundleCheckoutError() {
    const lines = (cart?.items || []).filter((item) => isBundle(item) && item.selling_plan_allocation);
    if (!lines.length) return '';
    const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
    if (quantity < bundleMinimum || lines.some((item) => item.properties?._fractal_bundle !== 'subscription')) {
      return `Bundle pricing requires at least ${bundleMinimum} items selected through Build a Bundle. Complete or remove your bundle before checkout.`;
    }
    return '';
  }
  const gift = (item) => /rebuy-(tiered-progress-bar|gift-with-purchase)/.test(JSON.stringify(item.properties || {}));
  const recommendationVariants = (p) => (p.variants || []).filter((v) => v.available
    && Number(v.price) > 0
    && !(config?.excludedRecommendationVariantIds || []).includes(v.id));

  async function request(path, body) {
    const response = await fetch(root + path, {
      signal:AbortSignal.timeout(12000),
      cache:path.startsWith('cart') ? 'no-store' : 'default',
      method:body ? 'POST' : 'GET',
      headers:{ Accept:'application/json', ...(body ? { 'Content-Type':'application/json' } : {}) },
      ...(body ? { body:JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Your cart could not be updated. Please try again.');
    return data;
  }
  async function product(handle) {
    if (!products.has(handle)) {
      products.set(handle, request(`products/${encodeURIComponent(handle)}.js`).catch(() => { products.delete(handle); return null; }));
    }
    return products.get(handle);
  }
  function error(message = '') {
    $('[data-fc-error]').textContent = message;
    $('[data-fc-error]').hidden = !message;
  }
  function setBusy(value) {
    busy = value;
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-fc-change], [data-fc-plan], [data-fc-add], [data-fc-variant], [data-fc-checkout]').forEach((el) => { el.disabled = value || el.dataset.unavailable === 'true'; });
    const express = $('.fc__express');
    if (express) express.inert = value;
  }
  function announce() {
    window.dispatchEvent(new CustomEvent('cart-count-updated', { detail:cart.item_count }));
    $('[data-fc-status]').textContent = `${cart.item_count} items in your cart. Subtotal ${money(cart.total_price)}.`;
  }

  function renderRewards() {
    const el = $('[data-fc-rewards]');
    el.hidden = !config || cart.currency !== config.currency || config.design?.showRewards === false;
    if (el.hidden) return;
    const now = Date.now();
    const configured = Array.isArray(config.rewards) ? config.rewards.filter(r =>
      ['ACTIVE', 'SCHEDULED'].includes(r.status) && (!r.startsAt || Date.parse(r.startsAt) <= now) && (!r.endsAt || Date.parse(r.endsAt) > now)
    ) : [ ...(config.shipping ? [{ ...config.shipping, kind:'shipping', label:'Free US shipping', countries:[config.shipping.country] }] : []), ...(config.tiers || []) ];
    const design = config.design || {};
    const tiers = configured.filter(r => r.kind !== 'shipping' || design.limitCountries === false || !r.countries?.length || r.countries.includes(drawer.dataset.country)).map(r => ({...r, shipping:r.kind === 'shipping'})).sort((a,b) => a.minimum-b.minimum);
    if (!tiers.length) { el.hidden = true; el.replaceChildren(); return; }
    const basis = design.excludeDiscounts ? cart.total_price : cart.original_total_price;
    const amountFor = (tier) => tier.shipping ? cart.total_price : basis;
    const applied = (tier) => !tier.shipping && (
      (cart.items || []).some((item) => (item.line_level_discount_allocations || []).some((d) => d.discount_application?.title === (tier.discountTitle || tier.label)))
      || (cart.cart_level_discount_applications || []).some((d) => d.title === (tier.discountTitle || tier.label))
    );
    // Shopify applies the best eligible discount, not every lower tier. Those
    // earlier milestones remain reached even when their allocations disappear.
    // An applied discount also proves its threshold was met before its own
    // savings reduced the subtotal. Shipping still uses the actual net total.
    const appliedMinimum = Math.max(-1, ...tiers.filter(applied).map(tier => tier.minimum));
    const reached = (tier) => amountFor(tier) >= tier.minimum || (!tier.shipping && tier.minimum <= appliedMinimum);
    const nextIndex = tiers.findIndex(tier => !reached(tier));
    const next = tiers[nextIndex];
    const message = next
      ? `${money(next.minimum - amountFor(next))} away from ${next.label.toLowerCase()}${next.shipping ? '' : ' on eligible items'}.`
      : 'Reward thresholds reached. Your eligible savings appear below.';
    // Use the same milestone states for icons, messaging, and the bar. Clamp at
    // the previous reached icon if a newly applied discount lowers the subtotal.
    let progress = 100;
    if (nextIndex >= 0) {
      const previous = nextIndex ? tiers[nextIndex-1].minimum : 0;
      const start = nextIndex ? (nextIndex-.5)/tiers.length*100 : 0;
      const end = (nextIndex+.5)/tiers.length*100;
      const fraction = Math.max(0, Math.min(1, (amountFor(next)-previous)/(next.minimum-previous || 1)));
      progress = start + (end-start)*fraction;
    }
    const tierIcon = (tier) => {
      const choice = tier.id && design.tierIcons?.[tier.id];
      const custom = choice === 'custom' && imageURL(design.tierCustomIcons?.[tier.id]);
      return custom ? `<img src="${custom}" alt="" width="20" height="20">` : icon(ICONS[choice] ? choice : tier.shipping ? 'truck' : 'bag');
    };
    const tiersMarkup = `<div class="fc__milestone-scroll"><div class="fc__milestones" style="min-width:${tiers.length*70}px">
      <div class="fc__track" role="progressbar" aria-label="Cart reward progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress)}"><span style="width:${progress}%"></span></div>
      <div class="fc__tiers">${tiers.map((tier) => `<div class="fc__tier ${reached(tier) ? 'is-reached' : ''}"><strong>${escape(money(tier.minimum))}</strong><span class="fc__milestone-icon">${tierIcon(tier)}</span><span>${escape(tier.label)}</span></div>`).join('')}</div></div></div>`;
    el.innerHTML = design.tiersTemplateEnabled && design.tiersTemplate
      ? fill(design.tiersTemplate, { message:escape(message), progress:String(Math.round(progress)), tiers:tiersMarkup })
      : `<p class="fc__reward-message">${escape(message)}</p>${tiersMarkup}
      <p class="fc__fine">Discounts apply to eligible items; subscription plan pricing is preserved.${tiers.filter(t=>t.shipping).map(t=>` ${escape(t.label)}${t.maximumShippingPrice != null ? ` on rates up to ${escape(money(t.maximumShippingPrice))}` : ''}; confirmed at checkout.`).join('')}</p>`;
  }

  function render() {
    const focused = document.activeElement;
    const restore = focused?.closest('[data-fc-key]') ? { key:focused.closest('[data-fc-key]').dataset.fcKey, action:focused.dataset.fcAction } : null;
    drawer.querySelectorAll('[data-fc-count]').forEach((el) => { el.textContent = `(${cart.item_count})`; });
    $('[data-fc-items]').innerHTML = cart.items.length ? cart.items.map((item) => {
      const key = escape(item.key);
      const title = escape(item.product_title || item.title);
      const image = item.featured_image?.url || item.image;
      const props = Object.entries(item.properties || {}).filter(([name, value]) => !name.startsWith('_') && value != null && value !== '');
      const lockedQuantity = gift(item) || config?.excludedSubscriptionVariantIds.includes(item.variant_id);
      const rule = item.quantity_rule || {};
      const increment = rule.increment || 1;
      const minimum = rule.min || 1;
      const max = rule.max == null ? 10 : Math.min(10, rule.max);
      return `<article class="fc__item" data-fc-key="${key}">
        <a href="${productURL(item.handle)}" tabindex="-1" aria-hidden="true">${image ? `<img class="fc__image" src="${safeURL(image)}" alt="" width="80" height="100">` : ''}</a>
        <div><div class="fc__item-head"><a class="fc__item-title" href="${productURL(item.handle)}">${title}</a><span class="fc__price">${item.original_line_price > item.final_line_price ? `<s>${escape(money(item.original_line_price))}</s>` : ''}${escape(money(item.final_line_price))}</span></div>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="fc__variant">${escape(item.variant_title)}</p>` : ''}
        ${props.map(([name, value]) => `<p class="fc__property">${escape(name)}: ${escape(value)}</p>`).join('')}
        ${item.selling_plan_allocation ? `<p class="fc__plan-label">${escape(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
        <div data-fc-plan-slot></div>
        <div class="fc__item-controls">${lockedQuantity ? `<span class="fc__variant">Quantity: ${item.quantity}</span>` : `<div class="fc__quantity"><button type="button" data-fc-change="${item.quantity - increment < minimum ? 0 : item.quantity - increment}" data-fc-action="decrease" aria-label="Decrease ${title}">−</button><span>${item.quantity}</span><button type="button" data-fc-change="${item.quantity + increment}" data-fc-action="increase" data-unavailable="${item.quantity + increment > max}" ${item.quantity + increment > max ? 'disabled' : ''} aria-label="Increase ${title}">+</button></div>`}
        <button type="button" class="fc__remove" data-fc-change="0" data-fc-action="remove" aria-label="Remove ${title}">Remove</button></div></div>
      </article>`;
    }).join('') : `<div class="fc__empty"><p>Your cart is empty.</p><p class="fc__fine">Add your favorite items to get started.</p><a href="${root}collections/all">Explore the forest</a></div>`;
    $('[data-fc-footer]').hidden = !cart.item_count;
    $('[data-fc-subtotal]').textContent = money(cart.total_price);
    // Shopify's original total already includes selling-plan pricing. Add back
    // only the plan savings so the comparison includes subscriptions as well as
    // line/cart discounts. Checkout always uses Shopify's actual total_price.
    const originalSubtotal = cart.original_total_price + cart.items.reduce((sum, item) => {
      const comparison = Number(item.selling_plan_allocation?.compare_at_price) || 0;
      return sum + Math.max(0, comparison * item.quantity - item.original_line_price);
    }, 0);
    const originalSubtotalLabel = $('[data-fc-original-subtotal]');
    if (originalSubtotalLabel) {
      originalSubtotalLabel.hidden = originalSubtotal <= cart.total_price;
      originalSubtotalLabel.textContent = originalSubtotal > cart.total_price ? money(originalSubtotal) : '';
    }
    const discounts = new Map();
    for (const item of cart.items) for (const d of item.line_level_discount_allocations || []) {
      const title = d.discount_application?.title || 'Discount';
      discounts.set(title, (discounts.get(title) || 0) + d.amount);
    }
    for (const d of cart.cart_level_discount_applications || []) discounts.set(d.title, (discounts.get(d.title) || 0) + d.total_allocated_amount);
    $('[data-fc-discounts]').innerHTML = [...discounts].map(([title, amount]) => `<div>${escape(title)} <span>−${escape(money(amount))}</span></div>`).join('');
    renderRewards();
    if (restore?.action) {
      const row = [...drawer.querySelectorAll('[data-fc-key]')].find((el) => el.dataset.fcKey === restore.key);
      row?.querySelector(`[data-fc-action="${restore.action}"]`)?.focus({ preventScroll:true });
    }
    const current = ++revision;
    renderPlans(current);
    renderRecommendations(current);
    setBusy(busy);
    announce();
  }

  async function renderPlans(current) {
    await Promise.all(cart.items.map(async (item) => {
      if (config?.design?.showPlans === false || isBundle(item) || gift(item) || item.final_price === 0 || config?.excludedSubscriptionVariantIds.includes(item.variant_id)) return;
      const p = await product(item.handle);
      if (!p || current !== revision) return;
      const variant = p.variants.find((v) => v.id === item.variant_id);
      const group = p.selling_plan_groups.find((g) => /^subscribe and save$/i.test(g.name));
      if (!group || !variant) return;
      const plans = group.selling_plans.filter((plan) => plan.recurring_deliveries && variant.selling_plan_allocations.some((a) => a.selling_plan_id === plan.id));
      if (!plans.length) return;
      const selected = item.selling_plan_allocation?.selling_plan.id || 0;
      // Leave legacy subscription lines intact; never silently migrate plans.
      if (selected && !plans.some((p) => p.id === selected)) return;
      const row = [...drawer.querySelectorAll('[data-fc-key]')].find((el) => el.dataset.fcKey === item.key);
      if (!row) return;
      const options = [
        ...(!p.requires_selling_plan ? [{ id:0, label:'Pay full price', price:variant.price, cadence:'One-time purchase' }] : []),
        ...plans.map((plan) => {
          const allocation = variant.selling_plan_allocations.find((a) => a.selling_plan_id === plan.id);
          const savings = variant.price > 0 ? Math.max(0, Math.round((1 - allocation.price / variant.price) * 100)) : 0;
          return { id:plan.id, label:savings ? `Subscribe and save ${savings}%` : 'Subscribe', price:allocation.price, cadence:plan.name };
        }),
      ];
      const optionMarkup = (option) => `<span class="fc__purchase-label">${escape(option.label)}</span><strong class="fc__purchase-price">${escape(money(option.price))}</strong><span class="fc__purchase-cadence">${escape(option.cadence)}</span>`;
      row.querySelector('[data-fc-plan-slot]').innerHTML = `<details class="fc__purchase" data-fc-purchase>
        <summary aria-label="Purchase option for ${escape(item.product_title)}" data-fc-action="plan">${optionMarkup(options.find((o) => o.id === selected) || options[0])}<span class="fc__purchase-arrow" aria-hidden="true">⌄</span></summary>
        <div class="fc__purchase-options" role="group" aria-label="Purchase options for ${escape(item.product_title)}">${options.map((option) => `<button type="button" data-fc-plan="${option.id}" aria-pressed="${selected === option.id}" ${busy ? 'disabled' : ''}>${optionMarkup(option)}</button>`).join('')}</div>
      </details>`;
    }));
  }

  async function renderRecommendations(current) {
    const el = $('[data-fc-recommendations]');
    // Do not leave recommendations for the previous cart actionable during fetch.
    el.hidden = true;
    el.replaceChildren();
    if (!config || config.design?.showRecommendations === false) { el.hidden = true; return; }
    const snapshot = cart;
    const ids = new Set(snapshot.items.map((i) => i.product_id));
    const rules = config.rules.filter((r) => r.when === 'empty' ? !snapshot.item_count : r.productIds.some((id) => ids.has(id)));
    const handles = [...new Set(rules.flatMap((r) => r.handles))];
    let candidates = (await Promise.all(handles.map(product))).filter(Boolean);
    const origins = new Map();
    for (const rule of rules) for (const handle of rule.handles) {
      if (!origins.has(handle)) origins.set(handle, { rule:rule.id, seeds:rule.productIds.filter((id) => ids.has(id)) });
    }
    const usable = (p) => !ids.has(p.id) && !p.requires_selling_plan && recommendationVariants(p).length > 0;
    candidates = candidates.filter(usable);
    if (candidates.length < 3 && snapshot.items.length) {
      const recs = await request(`recommendations/products.json?product_id=${snapshot.items[0].product_id}&limit=10&intent=related`).catch(() => ({ products:[] }));
      // Fetch the product endpoint for consistent availability and plan fields.
      const more = await Promise.all((recs.products || []).filter((p) => !ids.has(p.id)).slice(0,6).map((p) => product(p.handle)));
      for (const p of more.filter(Boolean)) if (!origins.has(p.handle)) origins.set(p.handle, { rule:'shopify-related', seeds:[snapshot.items[0].product_id] });
      candidates.push(...more.filter((p) => p && usable(p)));
    }
    if (candidates.length < 3) candidates.push(...(await Promise.all(config.fallbackHandles.map(product))).filter((p) => p && usable(p)));
    const seen = new Set();
    candidates = candidates.filter((p) => { if (seen.has(p.id)) return false; seen.add(p.id); return true; }).slice(0,3);
    if (current !== revision) return;
    el.hidden = !candidates.length;
    el.innerHTML = `<h3 id="fc-recommendation-title">${escape(rules.at(-1)?.heading || 'You may also like')}</h3>${candidates.map((p) => {
      const variants = recommendationVariants(p);
      const v = variants[0];
      const origin = origins.get(p.handle) || { rule:'fallback-top-sellers', seeds:[] };
      return `<article class="fc__rec" data-fc-rec="${escape(p.handle)}" data-fc-rule="${escape(origin.rule || 'configured-rule')}" data-fc-seeds="${escape(origin.seeds.join(','))}"><a href="${productURL(p.handle)}" tabindex="-1" aria-hidden="true">${p.featured_image ? `<img src="${safeURL(p.featured_image)}" alt="" loading="lazy" width="66" height="80">` : ''}</a>
        <div><a class="fc__rec-title" href="${productURL(p.handle)}">${escape(p.title)}</a><p class="fc__rec-price" data-fc-rec-price>${escape(money(v.price))}</p>
        ${variants.length > 1 ? `<select class="fc__variant-select" data-fc-variant aria-label="Size for ${escape(p.title)}" ${busy ? 'disabled' : ''}>${variants.map((v) => `<option value="${v.id}" data-price="${v.price}">${escape(v.title)}</option>`).join('')}</select>` : ''}</div>
        <button class="fc__add" type="button" data-fc-add="${v.id}" aria-label="Add ${escape(p.title)}" ${busy ? 'disabled' : ''}>${escape(config.design?.addLabel || 'Add')}</button></article>`;
    }).join('')}`;
  }

  async function refresh() {
    if (busy) { refreshPending = true; return; }
    setBusy(true);
    try {
      await Promise.all([configPromise, bundleRulesPromise]);
      cart = await request('cart.js');
      render();
    } catch (e) { error(e.message); }
    finally { setBusy(false); if (refreshPending) { refreshPending = false; refresh(); } }
  }
  async function mutate(path, body, action) {
    if (busy) return;
    error();
    setBusy(true);
    try {
      const result = await request(path, body);
      // /add.js returns only added items, never a complete cart.
      cart = path === 'cart/change.js' ? result : await request('cart.js');
      render();
      window.dispatchEvent(new CustomEvent('cart-updated', { detail:{ source:'fractal-cart', cart } }));
      if (action === 'plan') $('[data-fc-close]').focus({ preventScroll:true });
    } catch (e) {
      error(e.message);
      // A request may have succeeded despite a lost response. Reconcile; never
      // retry a write automatically and accidentally add the product twice.
      try { cart = await request('cart.js'); render(); } catch (_) { /* Keep the error and existing cart. */ }
    } finally { setBusy(false); if (refreshPending) { refreshPending = false; refresh(); } }
  }
  function open() {
    if (!drawer.open) { returnFocus = document.activeElement; drawer.showModal(); document.documentElement.classList.add('fc-open'); }
    refresh();
  }
  function close() { drawer.close(); }
  drawer.addEventListener('close', () => { document.documentElement.classList.remove('fc-open'); returnFocus?.focus?.({ preventScroll:true }); });
  drawer.addEventListener('click', (event) => {
    if (event.target === drawer || event.target.closest('[data-fc-close]')) { close(); return; }
    const button = event.target.closest('button');
    if (!button || busy) return;
    if (button.hasAttribute('data-fc-plan')) {
      const key = button.closest('[data-fc-key]').dataset.fcKey;
      const line = cart.items.findIndex((i) => i.key === key) + 1;
      if (!line) return;
      const item = cart.items[line - 1];
      const sellingPlan = Number(button.dataset.fcPlan) || null;
      if ((item.selling_plan_allocation?.selling_plan.id || null) === sellingPlan) {
        button.closest('details').open = false;
        button.closest('details').querySelector('summary').focus();
        return;
      }
      const properties = { ...item.properties };
      // Keep recommendation and merchant properties independent of conversion attribution.
      // A downgrade explicitly clears credit; changing cadence alone is not an upgrade.
      if (!sellingPlan) {
        properties._fractal_cart_subscription = 'one_time';
      } else if (!item.selling_plan_allocation) {
        properties._fractal_cart_subscription = 'cart_select';
        properties._fractal_cart_subscription_environment = ['main', 'live'].includes(window.Shopify?.theme?.role) ? 'live' : 'preview';
        properties._fractal_cart_subscription_theme = String(window.Shopify?.theme?.id || 'unknown');
      }
      mutate('cart/change.js', { line, quantity:item.quantity, selling_plan:sellingPlan, properties }, 'plan');
    }
    if (button.hasAttribute('data-fc-change')) mutate('cart/change.js', { id:button.closest('[data-fc-key]').dataset.fcKey, quantity:Number(button.dataset.fcChange) });
    if (button.hasAttribute('data-fc-add')) {
      const row = button.closest('[data-fc-rec]');
      const theme = window.Shopify?.theme;
      // Private line properties survive checkout/Shop Pay and remain visible
      // on the Shopify admin order. Do not tag existing cart/PDP items.
      const properties = {
        _fractal_cart_source:'cart_recommendation',
        _fractal_cart_rule:row.dataset.fcRule,
        _fractal_cart_seed_products:row.dataset.fcSeeds,
        _fractal_cart_theme:String(theme?.id || 'unknown'),
        _fractal_cart_environment:['main', 'live'].includes(theme?.role) ? 'live' : 'preview',
        _fractal_cart_version:'1',
      };
      mutate('cart/add.js', { items:[{ id:Number(button.dataset.fcAdd), quantity:1, properties }] });
    }
  });
  drawer.addEventListener('keydown', (event) => {
    const purchase = event.target.closest('[data-fc-purchase][open]');
    if (event.key === 'Escape' && purchase) {
      event.preventDefault(); event.stopPropagation(); purchase.open = false; purchase.querySelector('summary').focus();
    }
  });
  drawer.addEventListener('change', (event) => {
    const select = event.target;
    if (select.matches('[data-fc-variant]')) {
      const row = select.closest('[data-fc-rec]');
      row.querySelector('[data-fc-add]').dataset.fcAdd = select.value;
      row.querySelector('[data-fc-rec-price]').textContent = money(Number(select.selectedOptions[0].dataset.price));
    }
  });
  $('[data-fc-checkout-form]').addEventListener('submit', (event) => {
    const message = bundleCheckoutError();
    if (busy || !cart?.item_count || message) event.preventDefault();
    if (message) error(message);
  });
  // Capture the existing header before its Rebuy handler or Alpine toggle fires.
  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('#cart-button, [data-fractal-cart-open]');
    if (!target) return;
    event.preventDefault(); event.stopImmediatePropagation(); open();
  }, true);
  window.addEventListener('toggle-cart', open);
  window.addEventListener('cart-updated', (event) => { if (event.detail?.source !== 'fractal-cart') refresh(); });
  window.addEventListener('pageshow', () => { if (drawer.open) refresh(); });
  window.FractalCart = { open, close, refresh };
})();
